/**
 * Правило сверки задач с «Напоминаниями».
 *
 * Та же механика, что и у календаря: у каждой связанной пары хранится
 * отпечаток состояния на момент последней удачной сверки, и по нему видно,
 * кто менялся. Полей меньше — название, заметка, срок и галочка, — потому что
 * у напоминания нет ни длительности, ни места в сетке дня.
 *
 * Разница с календарём одна, и она важна: при споре побеждает не устройство, а
 * приложение. Календарь — договорённость с другими людьми, и переписывать её
 * личной правкой нельзя. Напоминания же личные: оба списка ведёт один человек,
 * и здесь он видит дело в контексте дня, бюджета и целей — то есть правит
 * осознаннее, чем на бегу в чужом приложении.
 *
 * Функции чистые: ни базы, ни плагина.
 */

import type { Task } from '@/shared/db/schema';

import type { ReminderFields, SystemReminder } from './reminders';

export type ReminderAction =
  /** Завести задачу: напоминание есть, пары здесь нет. */
  | { kind: 'createTask'; external: SystemReminder }
  /** Поправить задачу по напоминанию. */
  | { kind: 'updateTask'; local: Task; fields: ReminderFields }
  /** Поправить напоминание по задаче. */
  | { kind: 'updateReminder'; local: Task; fields: ReminderFields }
  /** Напоминание исчезло — убрать и задачу. */
  | { kind: 'deleteTask'; local: Task };

export interface ReminderPlan {
  actions: ReminderAction[];
}

export function fieldsOfTask(task: Task): ReminderFields {
  return {
    title: task.title,
    notes: task.notes,
    dueDate: task.dueDate,
    dueMin: task.dueMin,
    completed: task.status === 'done',
  };
}

export function fieldsOfReminder(reminder: SystemReminder): ReminderFields {
  return {
    title: reminder.title,
    notes: reminder.notes,
    dueDate: reminder.dueDate,
    dueMin: reminder.dueMin,
    completed: reminder.completed,
  };
}

/** Отпечаток: заметить изменение, а не защититься от подделки. */
export function fingerprintOf(fields: ReminderFields | SystemReminder): string {
  const text = [
    fields.title,
    fields.notes,
    fields.dueDate ?? '',
    fields.dueMin ?? '',
    fields.completed ? '1' : '0',
  ].join(' ');

  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = (hash + (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

export interface ReminderSyncInput {
  /** Задачи, пришедшие из «Напоминаний», — включая мягко удалённые. */
  local: Task[];
  external: SystemReminder[];
}

export function planReminderSync({ local, external }: ReminderSyncInput): ReminderPlan {
  const actions: ReminderAction[] = [];
  const byId = new Map(external.map((reminder) => [reminder.id, reminder]));
  const linked = new Set<string>();

  for (const task of local) {
    const reminderId = task.reminderId;
    if (reminderId === null) continue;

    linked.add(reminderId);
    const counterpart = byId.get(reminderId);

    if (!counterpart) {
      /*
        Напоминание удалили на устройстве. Задача, удалённая здесь, тоже сюда
        попадает — и для неё делать уже нечего: пары нет, и убирать нечего.
      */
      if (task.deletedAt === null) actions.push({ kind: 'deleteTask', local: task });
      continue;
    }

    if (task.deletedAt !== null) {
      // Задачу выбросили здесь — закрываем напоминание, а не удаляем его:
      // чужое приложение чистить за человека мы не вправе.
      actions.push({
        kind: 'updateReminder',
        local: task,
        fields: { ...fieldsOfReminder(counterpart), completed: true },
      });
      continue;
    }

    const shadow = task.reminderHash;
    const localFields = fieldsOfTask(task);
    const externalFields = fieldsOfReminder(counterpart);

    const localChanged = fingerprintOf(localFields) !== shadow;
    const externalChanged = fingerprintOf(externalFields) !== shadow;

    if (!localChanged && !externalChanged) continue;

    if (externalChanged && !localChanged) {
      actions.push({ kind: 'updateTask', local: task, fields: externalFields });
      continue;
    }

    // Менялось здесь — или менялось с обеих сторон. В обоих случаях уезжает
    // здешняя версия: список дел человек ведёт тут.
    actions.push({ kind: 'updateReminder', local: task, fields: localFields });
  }

  for (const reminder of external) {
    if (!linked.has(reminder.id)) actions.push({ kind: 'createTask', external: reminder });
  }

  return { actions };
}
